<form class="edh-filters" [attr.data-density]="density()" [formGroup]="emailDeliveryHubFiltersGroup">
  <div class="edh-filters__cluster edh-filters__cluster--primary-left" data-toolbar-cluster>
    <div class="d-flex align-items-center gap-2">
      <input
        dataSelector="min-date-input"
        aria-label="Email Delivery Hub range filter: Start date picker"
        class="form-input form-input--datepicker email-delivery-hub-filters-inputs-date-range"
        [class.form-input--invalid]="emailDeliveryHubFiltersGroup?.errors?.minAfterMax"
        [dpDayPicker]="clientCommunicationHubDateRangeMinPickerConfig"
        (keydown.enter)="$event.target.blur()"
        (keydown.escape)="$event.target.blur()"
        datePattern
        formControlName="min"
        placeholder="MM/DD/YYYY"
        separator="/"
      />
      <p>to</p>
      <input
        dataSelector="max-date-input"
        aria-label="Client Communication hub date range filter: End date picker"
        class="form-input form-input--datepicker email-delivery-hub-filters-inputs-date-range"
        [class.form-input--invalid]="emailDeliveryHubFiltersGroup?.errors?.minAfterMax"
        [dpDayPicker]="clientCommunicationHubDateRangeMaxPickerConfig"
        (keydown.enter)="$event.target.blur()"
        (keydown.escape)="$event.target.blur()"
        datePattern
        formControlName="max"
        placeholder="MM/DD/YYYY"
        separator="/"
      />
    </div>
  </div>

  <div class="edh-filters__cluster edh-filters__cluster--secondary-left email-filters-section-2 d-flex align-items-start gap-4" data-toolbar-cluster>
    @if (isSentTab()) {
      <aiwyn-autocomplete
        inputDataSelectorName="email-type-selector"
        class="email-delivery-hub-filters-inputs-type-selector"
        [multiple]="true"
        [options]="emailTypeOptions()"
        [suppressSort]="true"
        bindValue="All Email Types"
        controlName="types"
      />
    }

    @if (isSentTab()) {
      <div class="show-bounced-only-section d-flex align-items-center gap-2">
        <toggle dataSelector="show-bounced-only-toggle" formControlName="showBouncedOnly" label="Show bounced only" />
        @if (bouncedCount() > 0) {
          <pill dataSelector="bounced-count-pill" [value]="bouncedCount() | numberAbbr" color="red" size="small" />
        }
      </div>
    }

    @if (isNonSentTab()) {
      <toggle dataSelector="show-dismissed-resolved-toggle" formControlName="showDismissedAndResolved" label="Show dismissed & resolved" />
    }
  </div>

  <div class="edh-filters__cluster edh-filters__cluster--secondary-right d-flex align-items-center gap-4" data-toolbar-cluster>
    @if (isSentTab()) {
      <p
        dataSelector="last-refresh-timestamp"
        id="last-refresh-timestamp"
        class="body-400 text-nowrap d-flex align-items-center"
        [class.color-red-50]="lastRefreshError()"
        [tooltip]="nextUpdateTooltip()"
        [tooltipEnabled]="!!nextUpdateTooltip()"
      >
        Data last updated at {{ formattedLastRefresh() ?? 'unknown' }}
        @if (lastRefreshError()) {
          <anduin-icon
            class="ms-1"
            name="alert-triangle"
            [tooltip]="'The most recent data refresh failed. Displayed data may be out of date.'"
            [tooltipEnabled]="true"
          />
        }
      </p>
    }

    @if (showPmsSyncStatus()) {
      @let syncStatus = pmsSyncStatus();
      @let pollingState = pmsSyncPollingState();

      @if (pollingState === PmsSyncPollingState.POLLING) {
        <p
          dataSelector="pms-last-synced-timestamp"
          class="body-400 text-nowrap"
          [tooltip]="pmsSyncPollingTooltip()"
          [tooltipEnabled]="!!pmsSyncPollingTooltip()"
        >
          Checking for PMS sync...
        </p>
      } @else if (pollingState === PmsSyncPollingState.UNCONFIRMED || pollingState === PmsSyncPollingState.ERROR) {
        <p dataSelector="pms-last-synced-timestamp" class="body-400 text-nowrap d-flex align-items-center">
          Last synced at
          @if (syncStatus?.data?.lastSyncedAt) {
            <span class="ms-1">{{ formattedLastSyncedAt() }}</span>
          } @else {
            <span class="ms-1">unknown</span>
          }
          <anduin-icon
            class="ms-1"
            name="info"
            namespace="feather-icon"
            [tooltip]="pollingState === PmsSyncPollingState.ERROR
              ? 'An error occurred while checking for a PMS sync. The displayed time may be outdated.'
              : 'The PMS sync window has passed but no new data was detected. The sync may still be in progress, or no new data was available to ingest.'"
            [tooltipEnabled]="true"
          />
        </p>
      } @else if (syncStatus?.status === LoadableStatus.LOADED && syncStatus?.data) {
        <p dataSelector="pms-last-synced-timestamp" class="body-400 text-nowrap d-flex align-items-center">
          Last synced at
          @if (syncStatus.data.lastSyncedAt) {
            <span class="ms-1">{{ formattedLastSyncedAt() }}</span>
          } @else {
            <span class="ms-1">never</span>
          }
          @if (syncStatus.data.syncIntervalMs && syncStatus.data.nextSyncEstimatedAt) {
            <anduin-icon
              class="ms-1"
              name="info"
              [tooltip]="'Next sync estimated at ' + (syncStatus.data.nextSyncEstimatedAt | date: 'shortTime')"
              [tooltipEnabled]="true"
            />
          }
        </p>
      } @else if (syncStatus?.status === LoadableStatus.ERROR) {
        <p dataSelector="pms-last-synced-timestamp" class="body-400 text-nowrap color-red-50">
          Failed to load PMS sync status
        </p>
      } @else {
        <p dataSelector="pms-last-synced-timestamp" class="body-400 text-nowrap">
          Loading PMS sync status...
        </p>
      }
    }

    @if (hasActiveColumnFilters()) {
      <app-ag-grid-clear-column-filters-button class="text-nowrap" [gridApi]="gridApi()" />
    }
  </div>

  <div class="edh-filters__cluster edh-filters__cluster--primary-right d-flex align-items-center gap-4" data-toolbar-cluster>
    <input
      dataSelector="search-input"
      class="form-input form-input--search email-delivery-hub-filters-inputs-search"
      formControlName="search"
      placeholder="Search by anything…"
    />

    <ng-content select="[export-slot]" />
  </div>
</form>

@if (emailDeliveryHubFiltersGroup?.errors?.minAfterMax) {
  <mat-error class="d-block mt-1"> Start date should not be after the end date</mat-error>
}
